import { computed, ref } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import { NOTES_INBOX_QUERY } from '~/graphql/inbox/queries'
import { useAuthStore } from '@/stores/authStore'

/**
 * The NOTES inbox (V2) — module-level singleton, one poll for three sections.
 * -----------------------------------------------------------------------------
 * Follow-ups waiting to be read: cases (from the existing per-note tracking),
 * staff and contacts (from the read pointers this feature introduced). The case
 * section is the very payload `/follow-ups` already renders, so the two pages
 * can never disagree.
 *
 * Slower cadence than the messages inbox on purpose: a note is not a ringing
 * phone. Matches the 30s the V1 follow-ups page already polls at.
 */

const POLL_MS = 30_000
const POLL_MS_HIDDEN = 60_000

const EMPTY_TOTALS = { cases: 0, staff: 0, contacts: 0, all: 0 }

const _cases = ref([])
const _companies = ref([])
const _staff = ref([])
const _contacts = ref([])
const _totals = ref({ ...EMPTY_TOTALS })
const _loading = ref(false)
const _loaded = ref(false)

let _running = false
let _timer = null
let _inFlight = false
let _client = null
let _visibilityBound = false

async function _fetch(resolveClient) {
  if (_inFlight || !resolveClient) return
  _inFlight = true
  if (!_loaded.value) _loading.value = true
  try {
    const { data } = await resolveClient().query({
      query: NOTES_INBOX_QUERY,
      variables: { limit: 100 },
      fetchPolicy: 'network-only',
    })

    const p = data?.getNotesInbox ?? {}

    _cases.value = p.cases ?? []
    _companies.value = p.companies ?? []
    _staff.value = p.staff ?? []
    _contacts.value = p.contacts ?? []
    _totals.value = { ...EMPTY_TOTALS, ...(p.totals ?? {}) }
    _loaded.value = true
  }
  catch { /* transient — the next tick retries */ }
  finally {
    _inFlight = false
    _loading.value = false
  }
}

function _startTimer(interval = POLL_MS) {
  if (_timer || !_running || !_client) return
  _timer = setInterval(() => _fetch(_client), interval)
}

function _stopTimer() {
  if (_timer) {
    clearInterval(_timer)
    _timer = null
  }
}

/** Hidden tab keeps polling, just half as often — same deal as V1. */
function _onVisibility() {
  if (!_running) return
  _stopTimer()
  if (document.hidden) {
    _startTimer(POLL_MS_HIDDEN)

    return
  }
  _fetch(_client)
  _startTimer()
}

/** Reset on logout so the next user never inherits this badge. */
export function resetNotesInbox() {
  _running = false
  _stopTimer()

  if (_visibilityBound && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', _onVisibility)
    _visibilityBound = false
  }

  _client = null
  _cases.value = []
  _companies.value = []
  _staff.value = []
  _contacts.value = []
  _totals.value = { ...EMPTY_TOTALS }
  _loaded.value = false
}

export function useNotesInbox() {
  const { resolveClient } = useApolloClient()
  const authStore = useAuthStore()

  function start() {
    if (!authStore.isLeadAgencyUser) return

    _client = resolveClient
    if (_running) return
    _running = true

    if (typeof document !== 'undefined' && !_visibilityBound) {
      document.addEventListener('visibilitychange', _onVisibility)
      _visibilityBound = true
    }

    _fetch(_client)
    _startTimer()
  }

  function refresh() {
    if (authStore.isLeadAgencyUser) _fetch(_client ?? resolveClient)
  }

  /** Optimistic removal of one entity's card — see `useMessagesInbox.dismiss`. */
  function dismiss(section, match) {
    const lists = { cases: _cases, staff: _staff, contacts: _contacts }
    const list = lists[section]
    if (!list) return

    const row = list.value.find(match)
    if (!row) return

    list.value = list.value.filter(r => r !== row)

    // Case rows come from the tracking payload and carry `unreadCount`.
    const drop = row.count ?? row.unreadCount ?? 1

    _totals.value = {
      ..._totals.value,
      [section]: Math.max(0, (_totals.value[section] ?? 0) - drop),
      all: Math.max(0, (_totals.value.all ?? 0) - drop),
    }
  }

  /**
   * Unseen notes for ONE staff member / contact — the sidebar dots. Reads the
   * poll that is already running, so a list of 200 rows costs nothing extra.
   */
  function countFor(kind, entityId) {
    if (entityId == null) return 0
    const list = kind === 'staff' ? _staff.value : _contacts.value

    return (
      list.find(e => String(e.entityId) === String(entityId))?.count ?? 0
    )
  }

  return {
    cases: computed(() => _cases.value),
    companies: computed(() => _companies.value),
    staff: computed(() => _staff.value),
    contacts: computed(() => _contacts.value),
    countFor,
    totals: computed(() => _totals.value),
    total: computed(() => _totals.value.all ?? 0),
    loading: computed(() => _loading.value),
    loaded: computed(() => _loaded.value),
    start,
    refresh,
    dismiss,
    stop: resetNotesInbox,
  }
}
